/* Petifo · Fullscreen product media

   1. Kills the theme drag/pinch zoom (opens images at 1.5x + blocks touch).
   2. Lightbox = HORIZONTAL swipe carousel with prev/next arrows.
   3. Click (not drag) closes the lightbox.
   4. SWIPE TO DISMISS, up or down, modelled on iOS Photos / Instagram.
      Needs `touch-action:pan-x` on the gallery (set in pf-product-media) so
      vertical gestures reach us.
   5. Lightbox thumbnails follow the swiped image and get .pf-thumb-active.
   6. MAIN gallery thumbnails do the same when you swipe the main image.

   Arrow POSITION is pure CSS (screen edges) — the JS never sets inline
   left/right on the arrows.

   WHAT MAKES THE DISMISS FEEL NATIVE (rebuilt 2026-08-10, Peji: "den är inte så
   native, gör den mer native"). The first version faded the IMAGE and needed a
   flat 95px drag, which reads as a page half-dissolving:
   - The image stays fully opaque, follows the finger 1:1, drifts slightly on X
     and scales down toward 0.84. It is the DIALOG that fades, so the product
     page behind shows through — that is the whole illusion.
   - The chrome (close button, arrows, thumbnail rail) fades out during the drag;
     native viewers never leave controls floating over a moving photo.
   - VELOCITY counts. A short fast flick (>0.55 px/ms over 24px) dismisses just
     like a slow 110px drag. Distance alone always feels sticky.
   - Releasing below the threshold springs back on cubic-bezier(.22,1,.36,1),
     not `ease` — a decelerating spring, not a slide.
   - Dismissing ANIMATES OUT in the drag direction first, then closes. Closing
     on the spot makes the image vanish rather than leave.
   Set `transition:none` while the finger is down or every frame lags behind it.

   VELOCITY CLOCK: time the gesture with Date.now() ONLY. `e.timeStamp` is
   measured from the page's time origin, Date.now() from the epoch, so the
   idiom `e.timeStamp || Date.now()` silently mixes two clocks the moment
   timeStamp is 0 — dt then comes out around -1.7e12, `dt > 0` is never true,
   velocity stays 0 and the flick NEVER fires. Verified: an 86px/32ms flick
   sprang back instead of dismissing.

   FIVE HARD-WON RULES:
   a) NO markup strings in this file. Inline SVG here twice caused the following
      newline to arrive as a literal backslash-n, breaking minification and the
      whole script. The chevrons are drawn in CSS.
   b) NEVER scroll the lightbox with smooth behaviour: the dialog carries
      scroll-lock and smooth scrolls silently never complete.
   c) NEVER animate that scroll in steps: scroll-snap-type x mandatory snaps
      every intermediate frame back. Assign scrollLeft ONCE to the slide offset.
   d) CLOSING: never close the <dialog> underneath the theme, and never start a
      second close while one is running. Horizon's close is a ~500ms async
      sequence (rail slide-out, then a view transition that morphs the image
      back into the gallery slide). Cutting it short, or running it twice, is
      what made the close lag and the product image blank for a moment on
      desktop. Details on closeDialog().
   e) The thumbnail observer must NOT watch `class`, and must only act when the
      SELECTION actually changed: markActive() itself toggles a class, so
      watching `class` made the observer retrigger itself in a loop and re-force
      the scroll position every ~90ms — which made the thumbnail rail impossible
      to scroll by hand. Scrolling also only nudges the thumb into view when it
      is actually out of view; it never re-centres, so it cannot fight the user. */
(function () {
  'use strict';

  function qs(s, r) { return (r || document).querySelector(s); }
  function qsa(s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); }

  /* Nudge into view ONLY when out of view — never re-centre (see rule e) */
  function ensureVisible(el) {
    var p = el.parentElement;
    while (p && p !== document.body) {
      var sx = p.scrollWidth > p.clientWidth + 4;
      var sy = p.scrollHeight > p.clientHeight + 4;
      if (sx || sy) {
        if (sy) {
          var top = el.offsetTop;
          var bottom = top + el.offsetHeight;
          if (top < p.scrollTop) {
            p.scrollTop = Math.max(0, top - 8);
          } else if (bottom > p.scrollTop + p.clientHeight) {
            p.scrollTop = Math.min(p.scrollHeight - p.clientHeight, bottom - p.clientHeight + 8);
          }
        }
        if (sx) {
          var left = el.offsetLeft;
          var right = left + el.offsetWidth;
          if (left < p.scrollLeft) {
            p.scrollLeft = Math.max(0, left - 8);
          } else if (right > p.scrollLeft + p.clientWidth) {
            p.scrollLeft = Math.min(p.scrollWidth - p.clientWidth, right - p.clientWidth + 8);
          }
        }
        return;
      }
      p = p.parentElement;
    }
  }

  function markActive(list, el) {
    var old = qsa('.pf-thumb-active', list);
    old.forEach(function (n) { if (n !== el) n.classList.remove('pf-thumb-active'); });
    if (el && !el.classList.contains('pf-thumb-active')) el.classList.add('pf-thumb-active');
  }

  function killZoom() {
    qsa('drag-zoom-wrapper').forEach(function (w) {
      try { if (typeof w.destroy === 'function') w.destroy(); } catch (e) {}
      try { w.style.setProperty('--drag-zoom-scale', '1'); } catch (e) {}
    });
  }

  function ctx() {
    var dialog = qs('.dialog-zoomed-gallery__dialog');
    if (!dialog) return null;
    var gallery = qs('.dialog-zoomed-gallery', dialog);
    if (!gallery) return null;
    var items = qsa('.product-media-container', gallery);
    if (!items.length) return null;
    return {
      dialog: dialog,
      gallery: gallery,
      items: items,
      thumbs: qsa('.dialog-thumbnails-list__thumbnail', dialog),
      list: qs('.dialog-thumbnails-list', dialog)
    };
  }

  function indexOfView(c) {
    var w = c.gallery.clientWidth;
    if (!w) return 0;
    var i = Math.round(c.gallery.scrollLeft / w);
    return Math.max(0, Math.min(c.items.length - 1, i));
  }

  function goTo(c, i) {
    var w = c.gallery.clientWidth || 1;
    if (i < 0) i = 0;
    if (i > c.items.length - 1) i = c.items.length - 1;
    c.gallery.scrollLeft = i * w;
  }

  function updArrows(c) {
    var many = c.items.length > 1;
    var i = indexOfView(c);
    c.dialog.classList.toggle('pf-zf--single', !many);
    c.dialog.classList.toggle('pf-zf--first', many && i <= 0);
    c.dialog.classList.toggle('pf-zf--last', many && i >= c.items.length - 1);
  }

  var lastLightboxIdx = -1;

  function syncThumbs(c) {
    var i = indexOfView(c);
    c.thumbs.forEach(function (t, n) {
      var want = n === i ? 'true' : 'false';
      if (t.getAttribute('aria-selected') !== want) t.setAttribute('aria-selected', want);
    });
    if (c.list && c.thumbs[i]) {
      markActive(c.list, c.thumbs[i]);
      if (i !== lastLightboxIdx) {
        lastLightboxIdx = i;
        ensureVisible(c.thumbs[i]);
      }
    }
    updArrows(c);
  }

  function makeArrow(dir) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'pf-zf__arrow pf-zf__arrow--' + dir;
    b.setAttribute('aria-label', dir === 'prev' ? window.PetifoI18n.common.previous_image : window.PetifoI18n.common.next_image);
    return b;
  }

  function injectArrows(c) {
    if (qs('.pf-zf__arrow', c.dialog)) return;
    c.dialog.appendChild(makeArrow('prev'));
    c.dialog.appendChild(makeArrow('next'));
  }

  /* CLOSING — see rule (d).
     zoom-dialog.close() is ASYNC and has three stages: wait for the most
     visible image, slide the thumbnail rail out (it AWAITS that animation),
     then run a view transition that morphs the zoomed image back into the
     gallery slide and only then closes the <dialog>. Roughly half a second.
     Force-closing the dialog at 90ms cut that in half: the dialog vanished at
     once, and the view transition then ran against an element that was no
     longer rendered, so the product image blanked for the length of the
     transition. The fallback now sits well past the whole native sequence and
     only fires if it genuinely stalled.
     pfClosing also stops a second close from starting while one is running —
     two overlapping close() calls both add .dialog--closed, so the second one
     waits for a rail animation that never restarts and hangs forever. That is
     the "no-op" in rule (d): it was self-inflicted. */
  var pfClosing = false;

  function closeDialog(c, hard) {
    if (pfClosing) return;
    pfClosing = true;

    /* The swipe gesture has already animated the image off screen. Running the
       theme's morph-back on top of that would morph from somewhere invisible,
       so this path closes on the spot. */
    if (hard) {
      try { c.dialog.close(); } catch (e) {}
      pfClosing = false;
      return;
    }

    var zd = c.dialog.closest ? c.dialog.closest('zoom-dialog') : null;
    try { if (zd && typeof zd.close === 'function') zd.close(); } catch (e) {}
    setTimeout(function () {
      pfClosing = false;
      if (!c.dialog.hasAttribute('open')) return;
      try { c.dialog.close(); } catch (e) {}
    }, 900);
  }

  /* Clears every inline style the dismiss gesture writes. */
  function restoreChrome(c) {
    c.gallery.style.transition = '';
    c.gallery.style.transform = '';
    c.gallery.style.opacity = '';
    c.dialog.style.transition = '';
    c.dialog.style.opacity = '';
    var cb = qs('.dialog-zoomed-gallery__close-button', c.dialog);
    var els = [];
    if (cb) els.push(cb);
    if (c.list) els.push(c.list);
    qsa('.pf-zf__arrow', c.dialog).forEach(function (a) { els.push(a); });
    els.forEach(function (el) { el.style.transition = ''; el.style.opacity = ''; });
  }

  var SPRING = 'cubic-bezier(.22,1,.36,1)';

  function bindSwipeClose(c) {
    if (c.gallery.getAttribute('data-pf-swipe') === '1') return;
    c.gallery.setAttribute('data-pf-swipe', '1');

    var startX = 0, startY = 0, dx = 0, dy = 0;
    var lastY = 0, lastT = 0, vy = 0;
    var tracking = false, vertical = false;
    var chrome = [];

    function collectChrome() {
      var out = [];
      var cb = qs('.dialog-zoomed-gallery__close-button', c.dialog);
      if (cb) out.push(cb);
      if (c.list) out.push(c.list);
      qsa('.pf-zf__arrow', c.dialog).forEach(function (a) { out.push(a); });
      return out;
    }

    function fadeChrome(v, animate) {
      chrome.forEach(function (el) {
        el.style.transition = animate ? ('opacity .25s ' + SPRING) : 'none';
        el.style.opacity = String(v);
      });
    }

    /* Image follows the finger and shrinks; the DIALOG is what fades. */
    function paint() {
      var p = Math.min(1, Math.abs(dy) / 320);
      c.gallery.style.transition = 'none';
      c.dialog.style.transition = 'none';
      c.gallery.style.transform =
        'translate(' + (dx * 0.4) + 'px,' + dy + 'px) scale(' + (1 - p * 0.16) + ')';
      c.dialog.style.opacity = String(1 - p * 0.72);
    }

    function springBack() {
      c.gallery.style.transition = 'transform .32s ' + SPRING;
      c.dialog.style.transition = 'opacity .32s ' + SPRING;
      c.gallery.style.transform = '';
      c.dialog.style.opacity = '';
      fadeChrome(1, true);
      setTimeout(function () {
        c.gallery.style.transition = '';
        c.dialog.style.transition = '';
      }, 340);
    }

    function dismiss() {
      var dir = dy < 0 ? -1 : 1;
      var out = Math.max(window.innerHeight * 0.55, 340) * dir;
      c.gallery.style.transition = 'transform .2s ease-in, opacity .2s ease-in';
      c.dialog.style.transition = 'opacity .2s ease-in';
      c.gallery.style.transform = 'translateY(' + out + 'px) scale(.8)';
      c.gallery.style.opacity = '0';
      c.dialog.style.opacity = '0';
      setTimeout(function () {
        closeDialog(c, true);
        setTimeout(function () { restoreChrome(c); }, 80);
      }, 195);
    }

    c.gallery.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (e.target.closest && e.target.closest('.pf-zf__arrow')) return;
      startX = e.clientX;
      startY = e.clientY;
      lastY = e.clientY;
      lastT = Date.now();
      dx = 0; dy = 0; vy = 0;
      tracking = true;
      vertical = false;
      chrome = collectChrome();
    }, { passive: true });

    c.gallery.addEventListener('pointermove', function (e) {
      if (!tracking) return;
      var ddx = e.clientX - startX;
      var ddy = e.clientY - startY;

      if (!vertical) {
        if (Math.abs(ddy) > 12 && Math.abs(ddy) > Math.abs(ddx) * 1.3) {
          vertical = true;
          fadeChrome(0, true);
        } else if (Math.abs(ddx) > 12) {
          tracking = false;
          return;
        } else {
          return;
        }
      }

      /* ONE clock — see VELOCITY CLOCK in the header. */
      var now = Date.now();
      var dt = now - lastT;
      if (dt > 0) vy = (e.clientY - lastY) / dt;
      lastY = e.clientY;
      lastT = now;

      dx = ddx;
      dy = ddy;
      paint();
    }, { passive: true });

    function end() {
      if (!tracking) return;
      tracking = false;
      if (!vertical) return;
      /* Distance OR a flick — see header. */
      var flick = Math.abs(vy) > 0.55 && Math.abs(dy) > 24 && (vy > 0) === (dy > 0);
      if (Math.abs(dy) > 110 || flick) { dismiss(); return; }
      springBack();
    }

    c.gallery.addEventListener('pointerup', end, { passive: true });
    c.gallery.addEventListener('pointercancel', end, { passive: true });
  }

  function bindGallery(c) {
    bindSwipeClose(c);

    if (c.gallery.getAttribute('data-pf-zf') === '1') return;
    c.gallery.setAttribute('data-pf-zf', '1');

    var sx = 0, sy = 0, moved = false;
    c.gallery.addEventListener('pointerdown', function (e) {
      sx = e.clientX; sy = e.clientY; moved = false;
    }, { passive: true });
    c.gallery.addEventListener('pointermove', function (e) {
      var dx = Math.abs(e.clientX - sx);
      var dy2 = Math.abs(e.clientY - sy);
      if (dx > 10 || dy2 > 10) moved = true;
    }, { passive: true });
    c.gallery.addEventListener('click', function (e) {
      if (moved) return;
      if (!e.target.closest) return;
      if (e.target.closest('.pf-zf__arrow')) return;
      if (e.target.closest('video, iframe, model-viewer, button')) return;
      /* Image slides carry the theme's own on:click="/close", and Horizon binds
         those from a DOCUMENT listener in the CAPTURE phase (assets/component.js),
         so the theme has already started its close before this bubble-phase
         handler runs. Closing again from here ran zoom-dialog.close() twice —
         two overlapping view transitions, which is the flicker where the
         product image disappears for a moment. Let the theme own image clicks. */
      if (e.target.closest('.product-media-container--image')) return;
      closeDialog(c);
    });

    var raf = false;
    c.gallery.addEventListener('scroll', function () {
      if (raf) return;
      raf = true;
      requestAnimationFrame(function () {
        raf = false;
        var cc = ctx();
        if (cc) syncThumbs(cc);
      });
    }, { passive: true });
  }

  function reapply() {
    var cc = ctx();
    if (!cc) return;
    try { killZoom(); } catch (e) {}
    try { injectArrows(cc); } catch (e) {}
    try { bindGallery(cc); } catch (e) {}
    try { syncThumbs(cc); } catch (e) {}
  }

  function watchDialog(c) {
    if (c.dialog.getAttribute('data-pf-watch') === '1') return;
    c.dialog.setAttribute('data-pf-watch', '1');
    try {
      var mo = new MutationObserver(function () {
        if (!c.dialog.hasAttribute('open')) {
          pfClosing = false;
          restoreChrome(c);
          lastLightboxIdx = -1;
          return;
        }
        restoreChrome(c);
        [0, 120, 400, 900].forEach(function (ms) { setTimeout(reapply, ms); });
      });
      mo.observe(c.dialog, { attributes: true, attributeFilter: ['open'] });
    } catch (e) {}
  }

  /* MAIN gallery thumbnails — see rule (e) */
  function wireMainThumbs() {
    var groups = [];
    qsa('.slideshow-controls__thumbnail').forEach(function (t) {
      var p = t.parentElement;
      if (p && groups.indexOf(p) === -1) groups.push(p);
    });
    groups.forEach(function (g) {
      if (g.getAttribute('data-pf-follow') === '1') return;
      g.setAttribute('data-pf-follow', '1');

      var lastSel = null;
      var pending = false;

      var run = function () {
        if (pending) return;
        pending = true;
        setTimeout(function () {
          pending = false;
          var sel = g.querySelector('[aria-selected="true"]');
          if (!sel) sel = g.querySelector('[aria-current="true"]');
          if (!sel) sel = g.querySelector('[selected]');
          if (!sel) return;
          markActive(g, sel);
          if (sel === lastSel) return;   /* selection unchanged -> never touch scroll */
          lastSel = sel;
          ensureVisible(sel);
        }, 90);
      };

      try {
        var mo = new MutationObserver(run);
        /* NOTE: 'class' is deliberately NOT observed — markActive changes it. */
        mo.observe(g, {
          subtree: true,
          attributes: true,
          attributeFilter: ['aria-selected', 'aria-current', 'selected']
        });
      } catch (e) {}
      run();
    });
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;

    var arrow = e.target.closest('.pf-zf__arrow');
    if (arrow) {
      e.preventDefault();
      e.stopPropagation();
      var c = ctx();
      if (!c) return;
      var step = arrow.classList.contains('pf-zf__arrow--prev') ? -1 : 1;
      goTo(c, indexOfView(c) + step);
      syncThumbs(c);
      setTimeout(function () { syncThumbs(c); }, 220);
      return;
    }

    var thumb = e.target.closest('.dialog-thumbnails-list__thumbnail');
    if (thumb) {
      var c2 = ctx();
      if (!c2) return;
      var n = c2.thumbs.indexOf(thumb);
      if (n < 0) return;
      setTimeout(function () { goTo(c2, n); syncThumbs(c2); }, 0);
      setTimeout(function () { goTo(c2, n); syncThumbs(c2); }, 160);
    }
  }, true);

  function setup() {
    try { killZoom(); } catch (e) {}
    try { wireMainThumbs(); } catch (e) {}

    var c = null;
    try { c = ctx(); } catch (e) {}
    if (!c) return;

    try { injectArrows(c); } catch (e) {}
    try { bindGallery(c); } catch (e) {}
    try { watchDialog(c); } catch (e) {}
    try { syncThumbs(c); } catch (e) {}
  }

  var tries = 0;
  var iv = setInterval(function () {
    tries++;
    setup();
    if (tries > 60) clearInterval(iv);
  }, 350);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var hit = e.target.closest('.product-media-container--zoomable');
    if (!hit) hit = e.target.closest('.product-media-container__zoom-button');
    if (!hit) hit = e.target.closest('.slideshow-controls__thumbnail');
    if (!hit) return;
    [60, 260, 700, 1200].forEach(function (ms) { setTimeout(setup, ms); });
  }, true);
})();